import { useState, useRef, useContext, useEffect } from 'react'
import PropTypes from 'prop-types'
import { MarkupContext } from '../../providers/Markup'
import Section from './_Section'
import Picture from '../helpers/Picture'
import { transformPaddingToTailwind } from '../../helpers/style'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Pagination, Autoplay, A11y } from 'swiper/modules'
import { generateDataHash } from '../../helpers/contentHash'
import { ArrowRightIcon } from '@heroicons/react/24/outline'
import { dataLayerPush } from '../../helpers/thirdparty'
import ButtonEl from '../helpers/ctas/Button'
import TitleBlock from '../helpers/TitleBlock'

function TestimonialCard({ testimonial, index, testimonialCount, singleTestimonial = false, onPrevClick, onNextClick, createMarkup }) {
  return (
    <div
      className={`testimonial-card__wrapper group/testimonial ${index === 0 ? 'swiper-slide-active container' : ''} ${singleTestimonial ? 'testimonial-card_single' : ''}`}
    >
      {testimonialCount > 1 && (
        <div className="testimonial-card__navigation testimonial-card__navigation--prev invisible group-[:is(.swiper-slide-active_&)]/testimonial:visible">
          <button
            type="button"
            onClick={onPrevClick}
            className="testimonial-card__nav-button hover:scale-110 transform transition-transform duration-300 text-(--color-primary-text)"
            aria-label="Previous testimonial"
          >
            <span aria-hidden="true" className="rotate-180 inline-block">
              <ArrowRightIcon className="h-8 w-8" aria-hidden="true" />
            </span>
          </button>
        </div>
      )}

      <figure
        className="testimonial-card__figure flex flex-col gap-8"
        itemScope
        itemType="https://schema.org/Review"
        aria-label={testimonial.authorName ? `Testimonial from ${testimonial.authorName}` : 'Testimonial'}
      >
        <blockquote
          itemProp="reviewBody"
          className="testimonial-card__quote font-light text-(--sb-common-base-body-text-color) text-3xl md:text-4xl w-full max-w-full mx-auto"
        >
          <div data-rte="true" dangerouslySetInnerHTML={createMarkup(testimonial.quoteText)} />
        </blockquote>
        <figcaption className="testimonial-card__author">
          {(testimonial.desktopProfileImage || testimonial.mobileProfileImage) && (
            <div className="testimonial-card__image">
              <Picture
                desktopImage={testimonial.desktopProfileImage}
                mobileImage={testimonial.mobileProfileImage}
                className="testimonial-card__avatar rounded-full object-cover"
                itemProp="image"
                alt={
                  testimonial.desktopProfileImage?.altText ||
                  testimonial.mobileProfileImage?.altText ||
                  testimonial.authorName
                }
              />
            </div>
          )}
          <div className="testimonial-card__details" itemProp="author" itemScope itemType="https://schema.org/Person">
            <span className="testimonial-card__name font-semibold text-lg font-sans text-(--sb-common-base-body-text-color)" itemProp="name">
              {testimonial.authorName}
            </span>
            {(testimonial.authorTitle || testimonial.authorCompany) && (
              <div className="testimonial-card__meta text-base font-normal font-sans text-(--sb-common-base-body-text-color)!">
                {testimonial.authorTitle && (
                  <span className="testimonial-card__title" itemProp="jobTitle">
                    {testimonial.authorTitle}
                  </span>
                )}
                {testimonial.authorTitle && testimonial.authorCompany && ', '}
                {testimonial.authorCompany && (
                  <span
                    className="testimonial-card__company"
                    itemProp="worksFor"
                    itemScope
                    itemType="https://schema.org/Organization"
                  >
                    {testimonial.authorCompany}
                  </span>
                )}
              </div>
            )}
          </div>
        </figcaption>
      </figure>

      {testimonialCount > 1 && (
        <div className="testimonial-card__navigation testimonial-card__navigation--next invisible group-[:is(.swiper-slide-active_&)]/testimonial:visible">
          <button
            type="button"
            onClick={onNextClick}
            className="testimonial-card__nav-button hover:scale-110 transform transition-transform duration-300 text-(--color-primary-text)"
            aria-label="Next testimonial"
          >
            <span aria-hidden="true">
              <ArrowRightIcon className="h-8 w-8" aria-hidden="true" />
            </span>
          </button>
        </div>
      )}
    </div>
  )
}

TestimonialCard.propTypes = {
  testimonial: PropTypes.shape({
    quoteText: PropTypes.string,
    authorName: PropTypes.string,
    authorTitle: PropTypes.string,
    authorCompany: PropTypes.string,
    desktopProfileImage: PropTypes.object,
    mobileProfileImage: PropTypes.object,
  }).isRequired,
  index: PropTypes.number.isRequired,
  testimonialCount: PropTypes.number.isRequired,
  singleTestimonial: PropTypes.bool,
  onPrevClick: PropTypes.func,
  onNextClick: PropTypes.func,
  createMarkup: PropTypes.func.isRequired,
}

const DATALAYER_EVENT = 'carousel_interaction'
const DATALAYER_ACTION_PREV = 'previous'
const DATALAYER_ACTION_NEXT = 'next'
const DATALAYER_ACTION_DOT = 'dot'
const SWIPER_PAGINATION_BULLET_CLASS = 'swiper-pagination-bullet'

function TestimonialCarousel(props) {
  const { createMarkup } = useContext(MarkupContext)
  const componentPadding = transformPaddingToTailwind(props.componentPadding, false, props.breadcrumbs)
  const testimonials = props.testimonialItems || []
  const containerRef = useRef(null)
  const swiperRef = useRef(null)

  const [isPlaying, setIsPlaying] = useState(true)
  const testimonialCount = testimonials.length

  const handleToggleAutoplay = () => {
    if (!swiperRef.current) return
    if (isPlaying) {
      swiperRef.current.autoplay.stop()
      setIsPlaying(false)
    } else {
      swiperRef.current.autoplay.start()
      setIsPlaying(true)
    }
  }

  const handlePrevClick = (e) => {
    const newIndex = ((swiperRef.current?.realIndex ?? 0) - 1 + testimonialCount) % testimonialCount
    dataLayerPush({
      event: DATALAYER_EVENT,
      action: DATALAYER_ACTION_PREV,
      link_position: String(newIndex + 1),
    }, e.currentTarget)
    swiperRef.current?.slidePrev()
  }

  const handleNextClick = (e) => {
    const newIndex = ((swiperRef.current?.realIndex ?? 0) + 1) % testimonialCount
    dataLayerPush({
      event: DATALAYER_EVENT,
      action: DATALAYER_ACTION_NEXT,
      link_position: String(newIndex + 1),
    }, e.currentTarget)
    swiperRef.current?.slideNext()
  }

  useEffect(() => {
    const container = containerRef.current
    if (!container) return
    const handlePaginationClick = (e) => {
      if (!e.target?.classList?.contains(SWIPER_PAGINATION_BULLET_CLASS)) return
      const pagination = e.target.closest('.swiper-pagination')
      if (!pagination) return
      const bullets = Array.from(pagination.querySelectorAll(`.${SWIPER_PAGINATION_BULLET_CLASS}`))
      const dotIndex = bullets.indexOf(e.target)
      if (dotIndex === -1) return
      dataLayerPush({
        event: DATALAYER_EVENT,
        action: DATALAYER_ACTION_DOT,
        link_position: String(dotIndex + 1),
      }, e.target)
    }
    container.addEventListener('click', handlePaginationClick, true)
    return () => container.removeEventListener('click', handlePaginationClick, true)
  }, [])

  return (
    <Section type="testimonialCarousel" outerClass={`${componentPadding} overflow-hidden`} {...props} sectionTitle={false}>
      <div className="flex-1 container mx-auto [&_*]:!my-0">
            {(props.title || props.description) && (
              <TitleBlock
                {...props}                
              />
            )}
          </div>
      {testimonialCount > 0 && (
        <div
          className="testimonial-carousel"
          id={props.jumpToId}
          ref={containerRef}
          role="region"
          aria-label="Testimonials"
          aria-roledescription="carousel"
        >
          {testimonialCount === 1 && (
            <TestimonialCard
              testimonial={testimonials[0]}
              index={0}
              testimonialCount={testimonialCount}
              singleTestimonial
              onPrevClick={handlePrevClick}
              onNextClick={handleNextClick}
              createMarkup={createMarkup}
            />
          )}
          {testimonialCount > 1 && (
            <Swiper
              modules={[Pagination, Autoplay, A11y]}
              onSwiper={(swiper) => {
                swiperRef.current = swiper
              }}
              onAutoplayStop={() => setIsPlaying(false)}
              onAutoplayStart={() => setIsPlaying(true)}
              slidesPerView={1}
              spaceBetween={10}
              centeredSlides={true}
              speed={500}
              a11y={{
                enabled: true,
                prevSlideMessage: 'Previous testimonial',
                nextSlideMessage: 'Next testimonial',
              }}
              rewind={true}
              pagination={{ clickable: true }}
              breakpoints={{
                768: {
                  slidesPerView: 1.5,
                },
              }}
              autoplay={
                !props.autoPlay
                  ? false
                  : {
                      delay: 5000,
                      pauseOnMouseEnter: props.pauseOnHover,
                      disableOnInteraction: false,
                      waitForTransition: true,
                    }
              }
            >
              {testimonials.map((testimonial, index) => (
                <SwiperSlide key={generateDataHash(testimonial) ?? index}>
                  <TestimonialCard
                    testimonial={testimonial}
                    index={index}
                    testimonialCount={testimonialCount}
                    onPrevClick={handlePrevClick}
                    onNextClick={handleNextClick}
                    createMarkup={createMarkup}
                  />
                </SwiperSlide>
              ))}
            </Swiper>
          )}
          {/* Play/Pause button */}
          {props.autoPlay && testimonialCount > 1 && (
            <div className="flex justify-center mt-6">
              <ButtonEl
                theme="secondary"
                type="button"
                onClick={handleToggleAutoplay}
                label={isPlaying ? 'Pause Autoplay' : 'Resume Autoplay'}
                ariaPressed={!isPlaying}
              />

              <span
                className="sr-only"
                aria-live="polite"
                aria-atomic="true"
              >
                {isPlaying ? 'Autoplay is running' : 'Autoplay is paused'}
              </span>
            </div>
          )}
        </div>
      )}
    </Section>
  )
}

TestimonialCarousel.propTypes = {
  componentPadding: PropTypes.string,
  breadcrumbs: PropTypes.any,
  testimonialItems: PropTypes.arrayOf(
    PropTypes.shape({
      quoteText: PropTypes.string,
      authorName: PropTypes.string,
      authorTitle: PropTypes.string,
      authorCompany: PropTypes.string,
      desktopProfileImage: PropTypes.object,
      mobileProfileImage: PropTypes.object,
    }),
  ),
  jumpToId: PropTypes.string,
  autoPlay: PropTypes.bool,
  pauseOnHover: PropTypes.bool,
}

export default TestimonialCarousel
